/**
 * Grouped-list primitives, built on Telegram UI's conventions (Section wraps Cells on a
 * surface, hairline separators between rows, no shadows). Written locally rather than pulled
 * from @telegram-apps/telegram-ui because the design pins exact metrics that the library's
 * own tokens would fight — see DECISIONS.md D14.
 */

import type { ReactNode } from 'react';
import { avatarBackground, initialOf } from '../lib/format';
import { useI18n } from '../lib/i18n';
import { haptic } from '../hooks/useTg';
import { Icon, type IconName } from './Icon';

/* ── Section ─────────────────────────────────────────────────────────────── */

export function SectionHeader({ children }: { children: ReactNode }) {
  return (
    <div className="px-1 pb-2 text-footnote font-semibold uppercase tracking-wide text-hint">
      {children}
    </div>
  );
}

export function Section({
  header,
  footer,
  children,
  className = '',
}: {
  header?: ReactNode;
  footer?: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  return (
    <div className={className}>
      {header ? <SectionHeader>{header}</SectionHeader> : null}
      <div className="overflow-hidden rounded-card bg-surface">{children}</div>
      {footer ? <div className="px-1 pt-2 text-footnote text-hint">{footer}</div> : null}
    </div>
  );
}

/* ── Cell ────────────────────────────────────────────────────────────────── */

export interface CellProps {
  icon?: IconName;
  /** Custom leading element (avatar, thumbnail stack) — takes precedence over `icon`. */
  before?: ReactNode;
  title: ReactNode;
  subtitle?: ReactNode;
  /** Trailing value text, e.g. the current selection on a picker row. */
  value?: ReactNode;
  after?: ReactNode;
  badge?: ReactNode;
  chevron?: boolean;
  onClick?: () => void;
  /** Last row in a Section drops its separator. */
  last?: boolean;
  destructive?: boolean;
}

export function Cell({
  icon,
  before,
  title,
  subtitle,
  value,
  after,
  badge,
  chevron = false,
  onClick,
  last = false,
  destructive = false,
}: CellProps) {
  const interactive = Boolean(onClick);
  const Tag = interactive ? 'button' : 'div';

  return (
    <Tag
      {...(interactive
        ? {
            type: 'button' as const,
            onClick: () => {
              haptic('light');
              onClick?.();
            },
          }
        : {})}
      className={`flex min-h-tap w-full items-center gap-3 px-4 py-3 text-start transition-colors duration-fast ease-pv ${
        interactive ? 'active:bg-pressed-soft' : ''
      } ${last ? '' : 'hairline-b'}`}
    >
      {before ??
        (icon ? (
          <span className="flex h-[30px] w-[30px] flex-none items-center justify-center rounded-thumb bg-accent-tint text-accent">
            <Icon name={icon} size={17} />
          </span>
        ) : null)}

      <span className="min-w-0 flex-1">
        <span
          className={`block truncate text-body ${destructive ? 'text-destructive' : 'text-text'}`}
        >
          {title}
        </span>
        {subtitle ? (
          <span className="block truncate text-footnote text-hint">{subtitle}</span>
        ) : null}
      </span>

      {value ? (
        <span className="max-w-[150px] flex-none truncate text-body text-hint">{value}</span>
      ) : null}
      {badge}
      {after}
      {chevron ? <Icon name="chevron" size={19} className="flex-none text-chevron" /> : null}
    </Tag>
  );
}

export function CellBadge({ children }: { children: ReactNode }) {
  return (
    <span className="tnum flex-none rounded-full bg-button px-2 py-0.5 text-caption font-semibold text-button-text">
      {children}
    </span>
  );
}

/* ── Buttons ─────────────────────────────────────────────────────────────── */

export function Button({
  children,
  onClick,
  variant = 'primary',
  size = 'md',
  icon,
  disabled = false,
  className = '',
  type = 'button',
}: {
  children?: ReactNode;
  onClick?: () => void;
  variant?: 'primary' | 'secondary' | 'ghost' | 'dashed';
  size?: 'md' | 'lg';
  icon?: IconName;
  disabled?: boolean;
  className?: string;
  type?: 'button' | 'submit';
}) {
  const base =
    'inline-flex items-center justify-center gap-2 rounded-button font-medium transition-[background,transform] duration-fast ease-pv active:scale-[0.98] disabled:opacity-50';
  const sizes = { md: 'h-11 px-5 text-callout', lg: 'h-[50px] px-6 text-headline font-semibold' };
  const variants = {
    primary: 'bg-button text-button-text',
    secondary: 'bg-fill text-accent',
    ghost: 'text-accent active:bg-pressed-soft',
    dashed: 'border border-dashed border-accent-tint-strong text-accent',
  };

  return (
    <button
      type={type}
      disabled={disabled}
      onClick={() => {
        if (disabled) return;
        haptic('light');
        onClick?.();
      }}
      className={`${base} ${sizes[size]} ${variants[variant]} ${className}`}
    >
      {icon ? <Icon name={icon} size={18} /> : null}
      {children}
    </button>
  );
}

/** 44px circular icon button — used for header actions and the copy affordance. */
export function IconButton({
  name,
  onClick,
  label,
  size = 20,
  className = '',
  fill = false,
  testId,
}: {
  name: IconName;
  onClick?: () => void;
  label: string;
  size?: number;
  className?: string;
  fill?: boolean;
  /** A stable hook for the e2e harnesses — `label` is localised, so it cannot be selected on. */
  testId?: string;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      {...(testId ? { 'data-testid': testId } : {})}
      onClick={() => {
        haptic('light');
        onClick?.();
      }}
      className={`flex h-11 w-11 flex-none items-center justify-center rounded-full transition-[background,transform] duration-fast ease-pv active:scale-[0.92] active:bg-pressed-soft ${className}`}
    >
      <Icon name={name} size={size} fill={fill} />
    </button>
  );
}

/* ── Avatar ──────────────────────────────────────────────────────────────── */

export function Avatar({
  name,
  photoUrl,
  size = 38,
  seed,
}: {
  name: string;
  photoUrl?: string | null;
  size?: number;
  /** Overrides the hue source; defaults to the name. */
  seed?: string;
}) {
  const dim = { width: size, height: size };
  if (photoUrl) {
    return (
      <img
        src={photoUrl}
        alt=""
        style={dim}
        className="flex-none rounded-full object-cover"
        loading="lazy"
        decoding="async"
      />
    );
  }
  return (
    <span
      aria-hidden="true"
      style={{
        ...dim,
        background: avatarBackground(seed ?? name),
        fontSize: Math.round(size * 0.42),
      }}
      className="flex flex-none items-center justify-center rounded-full font-semibold text-on-media"
    >
      {initialOf(name)}
    </span>
  );
}

/* ── Toggle ──────────────────────────────────────────────────────────────── */

export function Toggle({
  checked,
  onChange,
  label,
}: {
  checked: boolean;
  onChange: (next: boolean) => void;
  label: string;
}) {
  const { rtl } = useI18n();
  // The track is laid out by flow direction, so in RTL the knob must travel the other way.
  const offset = checked ? (rtl ? -18 : 18) : 0;
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      onClick={() => {
        haptic('light');
        onChange(!checked);
      }}
      className={`flex h-7 w-[46px] flex-none items-center rounded-full p-0.5 transition-colors duration-300 ease-pv ${
        checked ? 'bg-button' : 'bg-track-off'
      }`}
    >
      <span
        className="h-6 w-6 rounded-full bg-surface transition-transform duration-300 ease-pv"
        style={{ transform: `translateX(${offset}px)` }}
      />
    </button>
  );
}

/* ── Segmented control ───────────────────────────────────────────────────── */

export function SegmentedControl<T extends string>({
  options,
  value,
  onChange,
  size = 'md',
}: {
  options: { value: T; label: ReactNode }[];
  value: T;
  onChange: (next: T) => void;
  size?: 'sm' | 'md';
}) {
  const pad = size === 'sm' ? 'px-3 py-1 text-footnote' : 'flex-1 px-3 py-1.5 text-subhead';
  // Class names must be literal for Tailwind's scanner to emit them.
  const track = size === 'sm' ? 'rounded-thumb' : 'rounded-button';
  return (
    <div role="tablist" className={`flex gap-0.5 ${track} bg-fill-strong p-0.5`}>
      {options.map((o) => {
        const active = o.value === value;
        return (
          <button
            key={o.value}
            type="button"
            role="tab"
            aria-selected={active}
            onClick={() => {
              haptic('light');
              onChange(o.value);
            }}
            className={`${pad} truncate rounded-thumb text-center transition-[background,color] duration-fast ease-pv ${
              active ? 'bg-surface font-semibold text-text' : 'font-medium text-hint'
            }`}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}
